import React, { useEffect, useState, useCallback } from "react";

/**
 * RawHubDashboard — embedded mirror of the RAW Company Hub.
 *
 * Polls /api/raw_hub/dashboard which is a backend composite that calls the
 * hub on our behalf (so secrets/passcodes never touch the browser).
 *
 * Designed to degrade gracefully — if any sub-block returns `_error` we show
 * a single-line warning per widget without breaking the layout.
 */
export default function RawHubDashboard({ dismiss }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchOnce = useCallback(async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/raw_hub/dashboard`);
      const j = await r.json();
      setData(j);
      setErr(null);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchOnce();
    // Per the hub's recommended cadence: refresh the composite every 60s
    const id = setInterval(fetchOnce, 60000);
    return () => clearInterval(id);
  }, [fetchOnce]);

  const widget = (label, content, error, testId) => (
    <div className="border border-[var(--rc-gold-dim)] p-3" data-testid={testId}>
      <div className="rc-label text-[9px] mb-1">{label}</div>
      {error ? (
        <div className="text-[10px] text-red-400 leading-tight">⚠ {error.slice(0, 90)}</div>
      ) : (
        <div className="font-display text-base rc-glow-text tabular-nums">{content}</div>
      )}
    </div>
  );

  const v = data || {};
  const signal = v.signal || {};
  const pnl = v.lifetime_pnl || {};
  const wr = v.win_rate_live || {};
  const bal = v.stripe_balance || {};
  const summary = v.stripe_summary || {};
  const family = v.family_feed || {};

  return (
    <div className="stage-in rc-panel relative p-5 max-w-4xl w-full" data-testid="stage-raw_hub">
      {dismiss && (
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 text-[var(--rc-gold)] hover:rc-glow-text text-lg leading-none"
          data-testid="dismiss-raw-hub"
        >×</button>
      )}
      <div className="font-display tracking-widest text-[var(--rc-gold)] rc-glow-text uppercase text-sm mb-3">
        // RAW-DATA HUB · LIVE MIRROR
      </div>
      {v.using_fallback && (
        <div className="mb-3 border border-orange-500/60 bg-orange-500/10 px-3 py-1.5 text-[10px] tracking-wide" data-testid="raw-hub-fallback-warning">
          ⚠ Production hub ({v.hub_url}) unreachable — using fallback {v.active_hub_url}
        </div>
      )}
      {loading && !data && <div className="text-xs opacity-60">Loading hub mirror…</div>}
      {err && <div className="text-xs text-red-400">{err}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {widget(
          "GOLD SIGNAL",
          <>
            <span className={signal.signal === "BUY" ? "text-green-400" : signal.signal === "SELL" ? "text-red-400" : ""}>
              {signal.signal || "—"}
            </span>
            <span className="text-[10px] block mt-1 opacity-70">{signal.strength || ""} · score {signal.score?.toFixed?.(1) ?? "—"}</span>
          </>,
          signal._error,
          "raw-hub-signal",
        )}
        {widget(
          "LIFETIME P&L",
          <>£{(pnl.total_pnl ?? pnl.pnl ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</>,
          pnl._error,
          "raw-hub-pnl",
        )}
        {widget(
          "WIN RATE (30D)",
          <>{(wr.win_rate_pct ?? wr.win_rate ?? 0).toFixed?.(1) ?? "—"}<span className="text-xs ml-0.5">%</span></>,
          wr._error,
          "raw-hub-wr",
        )}
        {widget(
          "STRIPE AVAILABLE",
          <>£{(bal.available_gbp ?? bal.available ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</>,
          bal._error,
          "raw-hub-stripe-bal",
        )}
        {widget(
          "STRIPE 7D",
          <>£{(summary.revenue_7d ?? summary.total ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</>,
          summary._error,
          "raw-hub-stripe-7d",
        )}
        {widget(
          "FAMILY APPS",
          <>{(family.apps?.length ?? family.count ?? 0)} <span className="text-xs">live</span></>,
          family._error,
          "raw-hub-family",
        )}
      </div>

      <div className="text-[9px] tracking-wider opacity-50 text-right">
        HUB · {v.active_hub_url || v.hub_url || "—"} · refreshes every 60s
      </div>
    </div>
  );
}
